/**
 * Utility functions for handling blueprint and glossary item versions
 * Supports both old (single number) and new (major.minor.patch) version formats
 */

import { GlossaryBlueprint } from "@/types-games";

/**
 * Extract a comparable version number from a blueprint
 * Handles both old format ({ version: number }) and new format ({ major, minor, patch })
 */
export function extractBlueprintVersion(blueprint: GlossaryBlueprint | null | undefined): number | undefined {
  if (!blueprint?.version) return undefined;

  const version = blueprint.version;

  // Check if it's the old format with a 'version' property
  if ('version' in version && typeof version.version === 'number') {
    return version.version;
  }

  // Check if it's the new format with major/minor/patch
  if ('major' in version && 'minor' in version && 'patch' in version) {
    // Convert major.minor.patch to a single comparable number
    // Format: major.minor.patch -> major * 1000000 + minor * 1000 + patch
    // This ensures proper comparison: 1.2.3 > 1.2.2, 2.0.0 > 1.999.999, etc.
    return version.major * 1000000 + version.minor * 1000 + version.patch;
  }

  return undefined;
}

/**
 * Compare two version numbers (from blueprints or items)
 * @returns 0 if equal, positive if v1 > v2, negative if v1 < v2
 */
export function compareVersionNumbers(v1: number | undefined, v2: number | undefined): number {
  if (v1 === undefined && v2 === undefined) return 0;
  if (v1 === undefined) return -1;
  if (v2 === undefined) return 1;
  
  // Use small epsilon for floating point comparison
  const diff = v1 - v2;
  if (Math.abs(diff) < 0.000001) return 0;
  return diff;
}

/**
 * Check if two versions match (within floating point tolerance)
 */
export function versionsMatch(v1: number | undefined, v2: number | undefined): boolean {
  return compareVersionNumbers(v1, v2) === 0;
}

/**
 * Extract version for saving to database
 * Returns the raw version value that should be stored in blueprint_version field
 */
export function getBlueprintVersionForSaving(blueprint: GlossaryBlueprint | null | undefined): number | { major: number; minor: number; patch: number } | null {
  if (!blueprint?.version) return null;

  const version = blueprint.version;

  // Return the version in whatever format it's already in
  // Old format: { version: number, changes: {} }
  if ('version' in version && typeof version.version === 'number') {
    return version.version;
  }

  // New format: { major: number, minor: number, patch: number }
  if ('major' in version && 'minor' in version && 'patch' in version) {
    return {
      major: version.major,
      minor: version.minor,
      patch: version.patch
    };
  }

  return null;
}

/**
 * Format version for display
 */
export function formatVersionForDisplay(version: number | { major: number; minor: number; patch: number } | undefined | null): string {
  if (!version) return 'No version';

  if (typeof version === 'number') {
    return version.toFixed(5);
  }

  if (typeof version === 'object' && 'major' in version) {
    return `${version.major}.${version.minor}.${version.patch}`;
  }

  return 'Unknown version';
}

/**
 * Format version (for backward compatibility with existing code)
 * Takes a Version type (major.minor.patch) and formats it
 */
export function formatVersion(version: { major: number; minor: number; patch: number }): string {
  return `${version.major}.${String(version.minor).padStart(3, '0')}.${String(version.patch).padStart(3, '0')}`;
}

/**
 * Compare two versions (for backward compatibility)
 * Returns 0 if equal, positive if v1 > v2, negative if v1 < v2
 */
export function compareVersions(
  v1: number | { major: number; minor: number; patch: number } | null | undefined,
  v2: number | { major: number; minor: number; patch: number } | null | undefined
): number {
  // Convert both to comparable numbers
  const num1 = typeof v1 === 'number' ? v1 : (v1 && 'major' in v1 ? v1.major * 1000000 + v1.minor * 1000 + v1.patch : 0);
  const num2 = typeof v2 === 'number' ? v2 : (v2 && 'major' in v2 ? v2.major * 1000000 + v2.minor * 1000 + v2.patch : 0);
  
  return compareVersionNumbers(num1, num2);
}

/**
 * Get time ago string (for backward compatibility)
 */
export function getTimeAgo(date: string): string {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  
  return past.toLocaleDateString();
}

/**
 * Get version badge color (for backward compatibility)
 */
export function getVersionBadgeColor(bumpType: 'major' | 'minor' | 'patch'): string {
  switch (bumpType) {
    case 'major': return 'bg-red-100 text-red-800 border-red-200';
    case 'minor': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'patch': return 'bg-green-100 text-green-800 border-green-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

/**
 * Determine bump type between two versions (for backward compatibility)
 */
export function determineBumpType(
  current: { major: number; minor: number; patch: number },
  previous: { major: number; minor: number; patch: number }
): 'major' | 'minor' | 'patch' {
  if (current.major > previous.major) return 'major';
  if (current.minor > previous.minor) return 'minor';
  return 'patch';
}

/**
 * Calculate next version (for backward compatibility)
 */
export function calculateNextVersion(
  current: { major: number; minor: number; patch: number },
  bumpType: 'major' | 'minor' | 'patch'
): { major: number; minor: number; patch: number } {
  switch (bumpType) {
    case 'major':
      return { major: current.major + 1, minor: 0, patch: 0 };
    case 'minor':
      return { major: current.major, minor: current.minor + 1, patch: 0 };
    case 'patch':
      return { major: current.major, minor: current.minor, patch: current.patch + 1 };
    default:
      return current;
  }
}
